import type { Component } from 'vue';

import {
  Access,
  Account,
  Alarm,
  Billing,
  Cloud,
  Cluster,
  Integration,
  Management,
  Support,
} from '@celerdata/icons';

// Sidebar navigation manifest.
//
// Why a standalone file (not buried in Sidebar.vue, not read from route meta):
// most of the host-shell paths (/clusters, /alert, /billing, …) currently
// delegate to legacy via the catch-all wujie route — there's no v3 router
// record to hang meta on. A standalone manifest is the closest thing to
// "single source of truth" we can offer until the views port over.
//
// When a view ports to v3 native, the entry here stays; its `path` just
// starts resolving to a v3 component instead of LegacyShellHost.
//
// To **add** an entry: append below. To **remove** it from the rendered
// sidebar without removing the entry: set `hidden: true` (preserves the
// declaration as documentation for what the host knows about the path).

export interface SubNavItem {
  path: string;
  langKey: string;
  /** Hide from sidebar rendering; entry stays declared. */
  hidden?: boolean;
}

export interface NavItem {
  path: string;
  langKey: string;
  icon: Component;
  /** Renders a thin divider line ABOVE this item — visual section separator. */
  divider?: boolean;
  children?: SubNavItem[];
  /** Hide from sidebar rendering; entry stays declared. */
  hidden?: boolean;
}

export const STATIC_NAV: NavItem[] = [
  { path: '/clusters', langKey: 'cluster', icon: Cluster },
  { path: '/cloud', langKey: 'cloud', icon: Cloud },
  {
    path: '/management',
    langKey: 'management',
    icon: Access,
    children: [
      { path: '/management/member', langKey: 'member' },
      { path: '/management/role', langKey: 'role' },
    ],
  },
  { path: '/alert', langKey: 'alarm', icon: Alarm },
  { path: '/billing', langKey: 'billing', icon: Billing },
  {
    path: '/account',
    langKey: 'account',
    icon: Account,
    children: [
      { path: '/account/setting', langKey: 'setting' },
      { path: '/account/audit-log', langKey: 'auditLog' },
      { path: '/account/organization', langKey: 'organization' },
    ],
  },
  { path: '/app-key', langKey: 'appKey', icon: Management },
  { path: '/integration', langKey: 'integration', icon: Integration },
  { path: '/support', langKey: 'support', icon: Support, divider: true },
];

/**
 * All CSP sub-paths the host knows about. The actual rendered children for
 * /cloud are derived at runtime from cspList ∩ regionList ∩ marketplace
 * sourceFrom (Sidebar.vue's `cloudChildren` computed). Kept here so the
 * manifest stays the single source for nav paths.
 */
export const CLOUD_CHILDREN_ALL: SubNavItem[] = [
  { path: '/cloud/aws', langKey: 'aws' },
  { path: '/cloud/azure', langKey: 'azure' },
  { path: '/cloud/gcp', langKey: 'gcp' },
];
